import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { OrganizationProvider } from "@/contexts/OrganizationContext";
import { CompareProvider } from "@/contexts/CompareContext";
import ProtectedRoute from "@/components/auth/ProtectedRoute";
import Auth from "@/pages/Auth";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import CampaignDetailPage from "./pages/CampaignDetailPage";
import CampaignKanban from "./components/CampaignKanban";
import TopPerformingInfluencers from "./pages/TopPerformingInfluencers";
import InfluencerProfilePage from "./pages/InfluencerProfilePage";
import InfluencerCRM from "./components/InfluencerCRM";
import DiscoveryPageMain from "./pages/DiscoveryPageMain";
import BrandManagement from "./components/BrandManagement";
import ContactsScreen from "./components/ContactsScreen";
import NotificationsScreen from "./components/NotificationsScreen";
import Analytics from "./components/Analytics";
import PaymentsScreen from "./components/PaymentsScreen";
import Settings from "./pages/Settings";
import EmailCenter from "./pages/EmailCenter";
import EmailHistoryDetail from "./pages/EmailHistoryDetail";
import UserManagement from "./pages/UserManagement";
import AcceptInvitation from "./pages/AcceptInvitation";
import Events from "./pages/Events";
import EventDetail from "./pages/EventDetail";
import Vendors from "./pages/Vendors";
import Calendar from "./pages/Calendar";
import SharedAnalyticsPage from "./pages/SharedAnalyticsPage";
import AuthenticatedLayout from "./components/AuthenticatedLayout";
import DiscoveryInfluencerProfile from "./pages/DiscoveryInfluencerProfile";
import SignUpStepOne from "./components/auth/SignUpStepOne";
import SignUpStepTwo from "./components/auth/SignUpStepTwo";
import GoogleOAuthCallback from "./pages/GoogleOAuthCallback";

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/auth" element={<Auth />} />
            <Route path="/auth/forgot-password" element={<ForgotPassword />} />
            <Route path="/auth/reset-password" element={<ResetPassword />} />
            <Route path="/signup" element={<SignUpStepOne />} />
            <Route path="/accept-invite" element={<AcceptInvitation />} />
            <Route path="/oauth/google/callback" element={<GoogleOAuthCallback />} />
            <Route path="/shared/analytics/:token" element={<SharedAnalyticsPage />} />
            <Route path="/onboarding" element={
              <ProtectedRoute>
                <SignUpStepTwo />
              </ProtectedRoute>
            } />
            <Route path="/*" element={
              <ProtectedRoute>
                <OrganizationProvider>
                  <CompareProvider>
                    <AuthenticatedLayout>
                      <Routes>
                        <Route path="/" element={<Index />} />
                        <Route path="/campaigns" element={<CampaignKanban />} />
                        <Route path="/campaigns/:id" element={<CampaignDetailPage />} />
                        <Route path="/influencers" element={<InfluencerCRM />} />
                        <Route path="/influencers/top" element={<TopPerformingInfluencers />} />
                        <Route path="/influencers/:id" element={<InfluencerProfilePage />} />
                        <Route path="/discovery" element={<DiscoveryPageMain />} />
                        <Route path="/discovery/:handle" element={<DiscoveryInfluencerProfile />} />
                        <Route path="/brands" element={<BrandManagement />} />
                        <Route path="/contacts" element={<ContactsScreen />} />
                        <Route path="/events" element={<Events />} />
                        <Route path="/events/:id" element={<EventDetail />} />
                        <Route path="/vendors" element={<Vendors />} />
                        <Route path="/notifications" element={<NotificationsScreen />} />
                        <Route path="/email-center" element={<EmailCenter />} />
                        <Route path="/email-center/history/:id" element={<EmailHistoryDetail />} />
                        <Route path="/analytics" element={<Analytics />} />
                        <Route path="/payments" element={<PaymentsScreen />} />
                        <Route path="/user-management" element={<UserManagement />} />
                        <Route path="/calendar" element={<Calendar />} />
                        <Route path="/settings" element={<Settings />} />
                        <Route path="*" element={<NotFound />} />
                      </Routes>
                    </AuthenticatedLayout>
                  </CompareProvider>
                </OrganizationProvider>
              </ProtectedRoute>
            } />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
